import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import "../styles/TreatmentDetailsPage.scss";
import ContactForm from "../Components/ContactForm";
import * as Accordion from "@radix-ui/react-accordion";
import {
  ChevronDown,
  Phone,
  MessageCircle,
  Calendar,
  Award,
  Users,
  AlertTriangle,
  Image,
  Eye,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import "../styles/Accordion.scss";
import {
  useTreatmentByName,
  useFooterData,
  useImagesByTreatmentName,
} from "../Apis/useApiQueries";
import { metaPixelEvents } from "../utils/metaPixel";

const TreatmentDetails = () => {
  const { name } = useParams();
  const contactFormRef = useRef(null);
  const {
    data: selectedTreatment,
    isLoading,
    error,
  } = useTreatmentByName(name);
  const { siteDetails } = useFooterData();

  // Treatment name mapping for before and after images
  const treatmentNameMapping = {
    // Hebrew treatment names that have before/after images
    "הזרקות בוטוקס": "טיפולי בוטוקס",
    "טיפולי בוטוקס": "טיפולי בוטוקס",
    בוטוקס: "טיפולי בוטוקס",
    "מילוי שפתיים": "פיסול שפתיים",
    "פיסול שפתיים": "פיסול שפתיים",
    שפתיים: "פיסול שפתיים",
    "מילוי שפם": "פיסול שפתיים",
    "פיסול אף": "פיסול אף",
    "מילוי אף": "פיסול אף",
    "נונסורג'יקל נוז ג'וב": "פיסול אף",
    "פיסול פנים": "פיסול פנים",
    "מילוי לחיים": "פיסול פנים",
    "הלבנת שיניים": "הלבנת שיניים",
    "יישור שיניים": "יישור שיניים",
    // All hair treatments show the same general category images
    "PRP לשיער": "טיפולי שיער",
    "PRP לשיער בריא": "טיפולי שיער",
    "טיפולי שיער": "טיפולי שיער",
    "מזותרפיה לשיער": "טיפולי שיער",
    "השתלת שיער": "טיפולי שיער",
    "טיפול בתאי גזע מלאכותיים": "טיפולי שיער",
    "PRP - פלזמה עשירה בטסיות": "טיפולי שיער",
    "תרופות לשיפור מצב השיער": "טיפולי שיער",
    "טיפול מזותרפיה לצמיחת שיער: הדרך המתקדמת לשיער בריא": "טיפולי שיער",
  };

  // Get the mapped treatment name for before/after images
  const beforeAfterTreatmentName =
    treatmentNameMapping[selectedTreatment?.name] ||
    treatmentNameMapping[selectedTreatment?.title] ||
    selectedTreatment?.name ||
    selectedTreatment?.title;

  // Fetch before/after images if available
  const { data: beforeAfterImages = [] } = useImagesByTreatmentName(
    beforeAfterTreatmentName
  );

  // Modal state for preview images
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Track treatment page view when data loads
  useEffect(() => {
    if (selectedTreatment) {
      metaPixelEvents.trackViewContent(
        selectedTreatment.title || selectedTreatment.name,
        selectedTreatment.treatmentRef || "treatment"
      );
    }
  }, [selectedTreatment]);

  // Handler functions for buttons

  const handleBookAppointment = () => {
    // For now, scroll to contact form. Later this could open a booking modal
    contactFormRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

    // Track button click
    metaPixelEvents.trackInitiateCheckout("hero_book_appointment");
  };

  const handlePhoneCall = () => {
    // Get phone number from site details or use default
    const phoneNumber = siteDetails?.data?.phone || "053-226-8138";
    window.open(`tel:${phoneNumber}`, "_self");

    // Track phone call
    metaPixelEvents.trackPhoneClick();
  };

  const handleSendMessage = () => {
    // Open WhatsApp with pre-filled message about the treatment
    const treatmentName =
      selectedTreatment?.title || selectedTreatment?.name || "טיפול";
    const whatsappMessage = encodeURIComponent(
      `שלום, אני מעוניין/ת לקבל מידע נוסף על ${treatmentName}`
    );
    window.open(
      `https://wa.me/+972532268138?text=${whatsappMessage}`,
      "_blank"
    );

    // Track message click
    metaPixelEvents.trackInitiateCheckout("quick_action_message");
  };

  const handleQuickBooking = () => {
    // Same as book appointment for now
    contactFormRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

    // Track quick booking
    metaPixelEvents.trackInitiateCheckout("quick_action_booking");
  };

  // Modal handlers for before/after images
  const openImageModal = (index) => {
    setCurrentImageIndex(index);
    setIsModalOpen(true);
    document.body.classList.add("prevent-scroll");
  };

  const closeImageModal = () => {
    setIsModalOpen(false);
    document.body.classList.remove("prevent-scroll");
  };

  const goToNextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % beforeAfterImages.length);
  };

  const goToPrevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? beforeAfterImages.length - 1 : prev - 1
    );
  };

  // Keyboard navigation for modal
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isModalOpen) return;

      switch (e.key) {
        case "Escape":
          closeImageModal();
          break;
        case "ArrowLeft":
          goToPrevImage();
          break;
        case "ArrowRight":
          goToNextImage();
          break;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, [isModalOpen, beforeAfterImages.length]);

  if (isLoading)
    return (
      <div className="treatment-loading">
        <div className="loading-spinner"></div>
        <p>טוען פרטי טיפול...</p>
      </div>
    );
  if (error)
    return (
      <div className="treatment-error">
        <h2>אופס! משהו השתבש</h2>
        <p>{error.message}</p>
      </div>
    );
  if (!selectedTreatment)
    return (
      <div className="treatment-not-found">
        <h2>טיפול לא נמצא</h2>
        <p>לא נמצאו פרטים על הטיפול המבוקש</p>
      </div>
    );

  const accordionItems = [
    {
      key: "expectedResults",
      content: selectedTreatment.expectedResults,
      title: "תוצאות צפויות",
      icon: Award,
    },
    {
      key: "howItWorks",
      content: selectedTreatment.howItWorks,
      title: "כיצד זה עובד?",
      icon: MessageCircle,
    },
    {
      key: "forWho",
      content: selectedTreatment.forWho,
      title: "למי מתאים הטיפול?",
      icon: Users,
    },
    {
      key: "sideEffects",
      content: selectedTreatment.sideEffects,
      title: "תופעות לוואי אפשריות",
      icon: AlertTriangle,
    },
  ];

  return (
    <div className="treatment-details-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 dir="auto" className="treatment-title">
              {selectedTreatment.title || selectedTreatment.name}
            </h1>
            <div className="hero-actions">
              <a
                href="https://wa.me/+972532268138"
                target="_blank"
                rel="noopener noreferrer"
                className="cta-button primary"
              >
                <FaWhatsapp className="button-icon" />
                צרו קשר עכשיו
              </a>
              <button
                className="cta-button secondary"
                onClick={handleBookAppointment}
              >
                <Calendar className="button-icon" />
                פנו אלינו
              </button>
            </div>
          </div>
        </div>
        <div className="hero-background"></div>
      </section>

      <div className="page-container">
        {/* Main Content */}
        <section className="main-content">
          {/* Treatment Information */}
          <div className="treatment-content">
            <div className="content-card">
              <h2 className="section-title">פרטי הטיפול</h2>
              <div
                dir="auto"
                className="treatment-description"
                dangerouslySetInnerHTML={{
                  __html: selectedTreatment.description,
                }}
              />
            </div>

            {/* Accordion Section */}
            {accordionItems.some((item) => item.content) && (
              <div className="content-card">
                <h2 className="section-title">מידע נוסף</h2>
                <Accordion.Root
                  type="single"
                  collapsible
                  className="modern-accordion"
                >
                  {accordionItems.map(
                    (item) =>
                      item.content && (
                        <Accordion.Item
                          key={item.key}
                          className="accordion-item"
                          value={item.key}
                        >
                          <Accordion.Header>
                            <Accordion.Trigger className="accordion-trigger">
                              <div className="trigger-content">
                                <item.icon className="accordion-trigger-icon" />
                                <span>{item.title}</span>
                              </div>
                              <ChevronDown className="accordion-icon" />
                            </Accordion.Trigger>
                          </Accordion.Header>
                          <Accordion.Content className="accordion-content">
                            <div
                              dangerouslySetInnerHTML={{
                                __html: item.content,
                              }}
                            />
                          </Accordion.Content>
                        </Accordion.Item>
                      )
                  )}
                </Accordion.Root>
              </div>
            )}
          </div>
        </section>

        {/* Before and After Section */}
        <section className="before-after-section">
          <div className="before-after-content">
            <div className="before-after-header">
              <h2>תוצאות לפני ואחרי</h2>
              <p>
                צפו בתוצאות המדהימות של{" "}
                {selectedTreatment?.title || selectedTreatment?.name}
              </p>
            </div>

            <div
              className={`before-after-gallery-preview ${
                beforeAfterImages.length === 0 ? "no-preview-images" : ""
              }`}
            >
              {beforeAfterImages.length > 0 && (
                <div className="preview-images">
                  {beforeAfterImages.slice(0, 3).map((imageUrl, index) => (
                    <div
                      key={index}
                      className="preview-image-container"
                      onClick={() => openImageModal(index)}
                    >
                      <img
                        src={imageUrl}
                        alt={`לפני ואחרי - ${
                          selectedTreatment?.title || selectedTreatment?.name
                        }`}
                        loading="lazy"
                      />
                      <div className="image-overlay">
                        <Eye className="overlay-icon" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="before-after-cta">
                <Link
                  to={`/before-and-after/${encodeURIComponent(
                    beforeAfterTreatmentName
                  )}`}
                  className="view-gallery-btn"
                >
                  <Image className="btn-icon" />
                  צפו בגלריה המלאה
                  <span className="btn-arrow">←</span>
                </Link>
                <p className="gallery-description">תוצאות של מטופלים</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section - Now Below Treatment Details */}
        <section className="contact-section" ref={contactFormRef}>
          <div className="contact-container">
            <div className="contact-content">
              <div className="contact-header-main">
                <h2>רוצים להתייעץ בנוגע לטיפול?</h2>
                <p>
                  נשמח לתת לכם מענה מהיר עם כל הפרטים. השאירו את פרטיכם ונחזור
                  אליכם בהקדם.
                </p>
              </div>

              <div className="contact-form-wrapper">
                <ContactForm />
              </div>
            </div>

            {/* Quick Actions Sidebar */}
            <div className="quick-actions-sidebar">
              <h3>פעולות מהירות</h3>
              <div className="action-buttons">
                <button className="action-button" onClick={handlePhoneCall}>
                  <Phone className="action-icon" />
                  התקשרו אלינו
                </button>
                <button className="action-button" onClick={handleSendMessage}>
                  <MessageCircle className="action-icon" />
                  שלחו הודעה
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Before/After Image Modal */}
      {isModalOpen && beforeAfterImages.length > 0 && (
        <div className="image-modal-overlay" onClick={closeImageModal}>
          <div className="image-modal-container">
            <div className="image-modal-header">
              <div className="modal-title">
                <span>
                  {selectedTreatment?.title || selectedTreatment?.name}
                </span>
                <span className="image-counter">
                  {currentImageIndex + 1} מתוך {beforeAfterImages.length}
                </span>
              </div>
              <button className="modal-close-button" onClick={closeImageModal}>
                <X />
              </button>
            </div>

            <div
              className="image-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                key={currentImageIndex}
                src={beforeAfterImages[currentImageIndex]}
                alt={`לפני ואחרי - ${
                  selectedTreatment?.title || selectedTreatment?.name
                }`}
                className="modal-image"
              />

              {beforeAfterImages.length > 1 && (
                <div className="modal-navigation-arrows">
                  <button
                    className="modal-arrow modal-prev-arrow"
                    onClick={goToPrevImage}
                    title="תמונה קודמת"
                  >
                    <ChevronRight />
                  </button>
                  <button
                    className="modal-arrow modal-next-arrow"
                    onClick={goToNextImage}
                    title="תמונה הבאה"
                  >
                    <ChevronLeft />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TreatmentDetails;
